import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { FaSave, FaEdit, FaUser, FaTimes } from 'react-icons/fa';
import { getAppISD, COUNTRIES } from './Settings';
const stripAnyISD = (phone) => {
  const sorted = [...COUNTRIES].sort((a, b) => b.isd.length - a.isd.length);
  for (const c of sorted) { if ((phone || '').startsWith(c.isd)) return phone.slice(c.isd.length); }
  return phone || '';
};
import { getFirebase } from '@services/firebase';
import { readUserCollection, writeUserDocument } from '@services/firebaseUser';
import { readGlobalProfiles, fetchCombinations, readFlatTemplate } from '@services/firebaseGlobal';

const { auth } = getFirebase();

const UserProfile = () => {
  const [user, authLoading, authError] = useAuthState(auth);
  const location = useLocation();
  const navigate = useNavigate();

  // Original profile state
  const [profile, setProfile] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    addressStreet: '',
    addressHouseNumber: '',
    addressPinCode: '',
    dateOfBirth: '',
    anniversary: '',
    profilePicture: '',
  });
  const [originalProfile, setOriginalProfile] = useState(null);
  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [isEditingDelivery, setIsEditingDelivery] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [userEnv, setUserEnv] = useState('Prod');
  const [formError, setFormError] = useState('');
  const [cakeInterests, setCakeInterests] = useState({});
  const [eventInterests, setEventInterests] = useState({});
  const [cakeSegments, setCakeSegments] = useState([]);
  const [events, setEvents] = useState([]);
  const [isEditingCakeInterest, setIsEditingCakeInterest] = useState(false);
  const [isEditingEventInterest, setIsEditingEventInterest] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [streetLoading, setStreetLoading] = useState(false);
  // Phone verification state
  const [upPhoneInput, setUpPhoneInput] = useState('');
  const [upPhoneEditMode, setUpPhoneEditMode] = useState(false);
  const [upPhoneSendingCode, setUpPhoneSendingCode] = useState(false);
  const [upPhoneCodeSent, setUpPhoneCodeSent] = useState(false);
  const [upPhoneResendCountdown, setUpPhoneResendCountdown] = useState(0);
  const [upPhoneCodeInput, setUpPhoneCodeInput] = useState('');
  const [upPhoneVerifying, setUpPhoneVerifying] = useState(false);
  const [upPhoneVerified, setUpPhoneVerified] = useState(false);
  const [upPhoneError, setUpPhoneError] = useState('');
  const [upIsdCode, setUpIsdCode] = useState(() => getAppISD());
  const locationDebounceRef = useRef(null);
  const streetDebounceRef = useRef(null);
  const allLocalitiesRef = useRef(null);
  const localitiesLoadingRef = useRef(false);
  const streetCacheRef = useRef({});
  const addrPinRawRef = useRef('');
  const addrCityRawRef = useRef('');

  useEffect(() => {
    const onCountryChange = () => setUpIsdCode(getAppISD());
    window.addEventListener('appCountryChanged', onCountryChange);
    window.addEventListener('storage', onCountryChange);
    return () => {
      window.removeEventListener('appCountryChanged', onCountryChange);
      window.removeEventListener('storage', onCountryChange);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const userList = await readGlobalProfiles('cUsers');
        const currentUserData = userList.find(u => u.id === user.uid);
        const adminStatus = currentUserData?.isadminuser || false;
        const superStatus = currentUserData?.issuperuser || false;
        setIsAdmin(adminStatus);
        setIsSuperUser(superStatus);

        const selectedUserId = location.state?.selectedUserId || user.uid;

        if (selectedUserId !== user.uid && !adminStatus && !superStatus) {
          navigate('/profile');
          return;
        }

        // Determine the selected user's env for orange-name display
        const selectedUserData = userList.find(u => u.id === selectedUserId);
        setUserEnv(selectedUserData?.env || 'Prod');

        const profileDataList = await readUserCollection('profile', selectedUserId);
        const profileData = profileDataList.length > 0 ? profileDataList[0] : null;

        let fetchedProfile = {
          email: user.email || '',
          firstName: '',
          lastName: '',
          phoneNumber: '',
          addressStreet: '',
          addressHouseNumber: '',
          addressPinCode: '',
          dateOfBirth: '',
          anniversary: '',
          profilePicture: '',
        };

        if (profileData) {
          let street = '', houseNumber = '', pinCode = '';
          if (profileData.address_primary?.length === 3) {
            // Preferred: structured array [street, houseNumber, pinCode]
            street = profileData.address_primary[0] || '';
            houseNumber = profileData.address_primary[1] || '';
            pinCode = profileData.address_primary[2] || '';
          } else {
            // Legacy fallback: flat address string
            const address = profileData.address || '';
            if (address) {
              const [streetHouse, pin] = address.split(',').map(part => part.trim());
              if (streetHouse && pin) {
                const houseIndex = streetHouse.lastIndexOf(' ');
                if (houseIndex !== -1) {
                  street = streetHouse.substring(0, houseIndex).trim();
                  houseNumber = streetHouse.substring(houseIndex + 1).trim();
                } else {
                  street = streetHouse;
                }
                pinCode = pin;
              }
            }
          }

          fetchedProfile = {
            ...fetchedProfile,
            email: profileData.email || fetchedProfile.email,
            firstName: profileData.f_name || '',
            lastName: profileData.l_name || '',
            phoneNumber: profileData.phone || '',
            addressStreet: street,
            addressHouseNumber: houseNumber,
            addressPinCode: pinCode,
            dateOfBirth: profileData.birth_day || '',
            anniversary: profileData.anniversary || '',
            profilePicture: profileData.p_picture || '',
          };
        }

        setProfile(fetchedProfile);
        setOriginalProfile(fetchedProfile);

        const fetchedCombinations = await fetchCombinations('oCombination');
        const rawSegs = fetchedCombinations.map(c => (c.segment || '').trim()).filter(Boolean);
        const _seenLC = new Set();
        const segments = rawSegs
          .filter(s => { const lc = s.toLowerCase(); if (_seenLC.has(lc)) return false; _seenLC.add(lc); return true; })
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        setCakeSegments(segments);
        setCakeInterests(Object.fromEntries(segments.map(f => [f, profileData?.cake_interests?.some(ci => ci.toLowerCase() === f.toLowerCase()) || false])));

        const eventDocs = await readFlatTemplate('cEvents');
        const validEvents = eventDocs.filter(e => e?.name).sort((a, b) => a.name.localeCompare(b.name));
        setEvents(validEvents);
        setEventInterests(Object.fromEntries(validEvents.map(e => [e.name, profileData?.event_interests?.includes(e.name) || false])));

      } catch (err) {
        console.error('Error loading profile:', err);
        setFormError('Failed to load profile data.');
      }
    };

    fetchData();
  }, [user, location.state, navigate]);

  const validatePersonalInfo = () => {
    const nameRegex = /^[A-Za-z\s-]+$/;
    if (isAdmin) {
      if (!profile.firstName || !nameRegex.test(profile.firstName)) return 'First Name must contain only letters, spaces, or hyphens.';
      if (!profile.lastName || !nameRegex.test(profile.lastName)) return 'Surname must contain only letters, spaces, or hyphens.';
    }
    if (profile.dateOfBirth) {
      const dob = new Date(profile.dateOfBirth);
      const today = new Date();
      if (isNaN(dob.getTime()) || dob > today) return 'Date of Birth must be a valid date and not in the future.';
    }
    if (profile.anniversary) {
      const anniv = new Date(profile.anniversary);
      if (isNaN(anniv.getTime())) return 'Anniversary must be a valid date.';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (isAdmin && (!profile.email || !emailRegex.test(profile.email))) return 'Please enter a valid email address.';
    return '';
  };

  const validateDeliveryInfo = () => {
    const postalCodeRegex = /^\d{5}$/;
    if (profile.addressStreet || profile.addressHouseNumber || profile.addressPinCode) {
      if (!profile.addressStreet) return 'Address Street is required.';
      if (!profile.addressHouseNumber || !/\d/.test(profile.addressHouseNumber)) return 'Address House Number must include a number.';
      const pinVal = addrPinRawRef.current || (/^\d{5}$/.test((profile.addressPinCode || '').trim()) ? profile.addressPinCode.trim() : '');
      if (!pinVal || !postalCodeRegex.test(pinVal)) return 'Address PIN Code must be a valid 5-digit number.';
    }

    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (type, name) => (e) => {
    const checked = e.target.checked;
    if (type === 'cake') setCakeInterests(prev => ({ ...prev, [name]: checked }));
    else setEventInterests(prev => ({ ...prev, [name]: checked }));
  };

  const handleStartEditPersonal = () => { setOriginalProfile({ ...profile }); setIsEditingPersonal(true); };
  const handleStartEditDelivery = () => {
    setOriginalProfile({ ...profile });
    addrPinRawRef.current = '';
    addrCityRawRef.current = '';
    setUpPhoneInput(stripAnyISD(profile.phoneNumber));
    setUpPhoneCodeSent(false);
    setUpPhoneCodeInput('');
    setUpPhoneError('');
    setUpPhoneVerified(false);
    setIsEditingDelivery(true);
  };
  const handleStartEditCakeInterest = () => { setOriginalProfile({ ...profile }); setIsEditingCakeInterest(true); };
  const handleStartEditEventInterest = () => { setOriginalProfile({ ...profile }); setIsEditingEventInterest(true); };

  const handleSave = async (section) => {
    try {
      let validationError = '';
      if (section === 'personal') validationError = validatePersonalInfo();
      else if (section === 'delivery') validationError = validateDeliveryInfo();
      if (validationError) { setFormError(validationError); return; }

      const selectedCakeInterests = Object.keys(cakeInterests).filter(key => cakeInterests[key]);
      const selectedEventInterests = Object.keys(eventInterests).filter(key => eventInterests[key]);
      const pinToSave = addrPinRawRef.current || (/^\d{5}$/.test((profile.addressPinCode || '').trim()) ? profile.addressPinCode.trim() : profile.addressPinCode);

      const savedLocalPart = stripAnyISD(profile.phoneNumber);
      const phoneChanged = section === 'delivery' && canEditPhone && upPhoneInput !== savedLocalPart;
      if (phoneChanged && !upPhoneVerified) {
        setFormError('Please verify your new phone number before saving.');
        return;
      }
      const newPhone = (section === 'delivery' && phoneChanged && upPhoneVerified) ? upIsdCode + upPhoneInput : profile.phoneNumber;

      const userData = {
        email: profile.email,
        f_name: profile.firstName,
        l_name: profile.lastName,
        phone: newPhone,
        address_primary: [profile.addressStreet || '', profile.addressHouseNumber || '', pinToSave || ''],
        birth_day: profile.dateOfBirth,
        anniversary: profile.anniversary,
        p_picture: profile.profilePicture,
        cake_interests: selectedCakeInterests,
        event_interests: selectedEventInterests,
      };

      const selectedUserId = location.state?.selectedUserId || user.uid;
      const profileDataList = await readUserCollection('profile', selectedUserId);
      const docId = profileDataList.length > 0 ? profileDataList[0].id : null;

      if (docId) {
        await writeUserDocument('profile', selectedUserId, userData, docId);
      } else {
        await writeUserDocument('profile', selectedUserId, userData);
      }

      if (section === 'personal') setIsEditingPersonal(false);
      else if (section === 'delivery') {
        setIsEditingDelivery(false);
        addrPinRawRef.current = '';
        addrCityRawRef.current = '';
        setUpPhoneInput('');
        setUpPhoneCodeSent(false);
        setUpPhoneCodeInput('');
        setUpPhoneError('');
        setUpPhoneVerified(false);
        if (phoneChanged && upPhoneVerified) setProfile(prev => ({ ...prev, phoneNumber: newPhone }));
      }
      else if (section === 'cake') setIsEditingCakeInterest(false);
      else if (section === 'event') setIsEditingEventInterest(false);
      setOriginalProfile(prev => (section === 'delivery' && phoneChanged && upPhoneVerified) ? { ...prev, phoneNumber: newPhone } : { ...prev });
      setFormError('');
    } catch (err) {
      setFormError('Failed to save profile changes.');
    }
  };

  const handleCancelPersonal = () => { if (originalProfile) setProfile(originalProfile); setIsEditingPersonal(false); setFormError(''); };
  const handleCancelDelivery = () => {
    if (originalProfile) setProfile(originalProfile);
    setIsEditingDelivery(false);
    addrPinRawRef.current = '';
    addrCityRawRef.current = '';
    setLocationSuggestions([]);
    setStreetSuggestions([]);
    setUpPhoneInput('');
    setUpPhoneCodeSent(false);
    setUpPhoneCodeInput('');
    setUpPhoneError('');
    setUpPhoneVerified(false);
    setFormError('');
  };
  const handleCancelCakeInterest = () => { setIsEditingCakeInterest(false); setFormError(''); };
  const handleCancelEventInterest = () => { setIsEditingEventInterest(false); setFormError(''); };

  const handleUpSendPhoneCode = async () => {
    if (!upPhoneInput || upPhoneInput.length < 5) return;
    setUpPhoneSendingCode(true);
    setUpPhoneError('');
    try {
      const targetId = location.state?.selectedUserId || user.uid;
      const fullPhone = upIsdCode + upPhoneInput;
      const response = await fetch(
        'https://us-central1-pcakedatastore.cloudfunctions.net/sendPhoneVerificationCode',
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: targetId, phoneNumber: fullPhone }) }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error?.message || 'Failed to send code');
      setUpPhoneCodeSent(true);
      setUpPhoneError('Code sent via SMS.');
      setUpPhoneResendCountdown(30);
      const interval = setInterval(() => {
        setUpPhoneResendCountdown(prev => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setUpPhoneError(err.message || 'Failed to send verification code.');
    } finally {
      setUpPhoneSendingCode(false);
    }
  };

  const handleUpVerifyPhoneCode = async (codeOverride) => {
    const code = codeOverride ?? upPhoneCodeInput;
    if (code.length !== 6) return;
    setUpPhoneVerifying(true);
    setUpPhoneError('');
    try {
      const targetId = location.state?.selectedUserId || user.uid;
      const fullPhone = upIsdCode + upPhoneInput;
      const response = await fetch(
        'https://us-central1-pcakedatastore.cloudfunctions.net/verifyPhoneCode',
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: targetId, phoneNumber: fullPhone, code }) }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error?.message || 'Verification failed');
      setUpPhoneVerified(true);
      setUpPhoneError('');
    } catch (err) {
      setUpPhoneError('invalid');
    } finally {
      setUpPhoneVerifying(false);
    }
  };

  const handleClose = () => {
    const from = location.state?.from || 'profile';
    if (from === 'user-admin') {
      navigate('/user-admin', { state: { viewState: location.state?.viewState } });
    } else {
      navigate('/profile');
    }
  };

  if (authLoading) return <div>Loading...</div>;
  if (authError) return <div>Error: {authError.message}</div>;
  if (!user) return <div>Please log in to access this page.</div>;

  const selectedUserId = location.state?.selectedUserId || user.uid;
  const isSelf = selectedUserId === user.uid;
  const canEditPhone = (isAdmin || isSuperUser) || isSelf;
  const canEditPersonal = isAdmin || isSelf;
  const canEditDelivery = isAdmin || isSelf;

  const addressDisplay = [profile.addressStreet, profile.addressHouseNumber].filter(Boolean).join(' ');
  const fullAddress = addressDisplay ? `${addressDisplay}${profile.addressPinCode ? ', ' + profile.addressPinCode : ''}` : '';
  const minBirthday = new Date(); minBirthday.setFullYear(minBirthday.getFullYear() - 18);
  const minBirthdayStr = minBirthday.toISOString().split('T')[0];

  return (
    <>
      <style>{`
        /* Page Layout */
        .up-page-container {
          display: flex;
          flex-direction: column;
          padding: 5px;
          padding-bottom: 50px;
          background-color: #f5f5f5;
          width: 100%;
          box-sizing: border-box;
        }

        .up-page-content {
          position: relative;
          background: white;
          border-radius: 8px;
          width: 80vw;
          max-width: 500px;
          height: calc(100vh - 70px);
          padding: 15px;
          margin: 10px auto;
          display: flex;
          flex-direction: column;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          box-sizing: border-box;
        }

        .up-page-close-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          background: none;
          border: none;
          font-size: 16px;
          cursor: pointer;
          color: #666;
          z-index: 10;
        }

        .up-page-close-btn:hover {
          color: #333;
        }

        .up-profile-picture {
          display: flex;
          justify-content: center;
          margin-bottom: 10px;
        }

        .up-avatar {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 70px;
          height: 70px;
          border-radius: 50%;
          background-color: #ddd;
          color: #666;
          border: 1px solid #ccc;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .up-profile-pic {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          object-fit: cover;
          border: 1px solid #ccc;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .up-user-info {
          text-align: center;
          margin-bottom: 15px;
        }

        .up-user-email {
          font-size: 14px;
          color: #333;
          margin: 0;
        }

        .up-page-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 10px;
          overflow-y: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .up-page-body::-webkit-scrollbar {
          display: none;
        }

        .up-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: #f0f0f0;
          padding: 6px 10px;
          border-radius: 5px;
          margin-bottom: 5px;
        }

        .up-section-title {
          font-size: 16px;
          margin: 2px;
          color: #333;
          font-weight: 600;
        }

        .up-action-buttons {
          display: flex;
          gap: 5px;
          align-items: center;
        }

        .up-action-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 16px;
          padding: 2px;
          color: #666;
          transition: color 0.2s;
        }

        .up-action-btn:hover {
          color: #333;
        }

        .up-action-btn:disabled {
          color: #999;
          cursor: not-allowed;
        }

        .up-form-section {
          padding: 10px;
          background-color: #fff;
          border-radius: 5px;
          margin-bottom: 10px;
        }

        .up-form-columns {
          display: flex;
          gap: 10px;
        }

        .up-form-column {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .up-form-row {
          display: flex;
          gap: 4px;
          margin-bottom: 4px;
        }

        .up-form-row.no-gap {
          gap: 0;
        }

        .up-form-row.label-row {
          gap: 4px;
          align-items: center;
        }

        .up-form-group {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .up-form-label {
          font-size: 14px;
          color: #333;
          margin-right: 25px;
          margin-left: 8px;
          white-space: nowrap;
          min-width: 90px;
          text-align: left;
        }

        .up-input-field,
        .up-input-field[type="email"],
        .up-input-field[type="tel"],
        .up-input-field[type="date"] {
          padding: 5px 5px 3px 6px;
          border: none;
          border-radius: 0;
          font-size: 14px;
          width: 100%;
          box-sizing: border-box;
          background: transparent;
          color: #333;
          text-align: left;
          align-items: center;
        }

        .up-input-field[type="text"] {
          padding: 5px 5px 3px 6px;
          border: none;
          border-radius: 0;
          font-size: 14px;
          box-sizing: border-box;
          background: transparent;
          max-width: 200px;
          color: #333;
        }

        .up-input-field.long-field {
          max-width: 100%;
          min-width: 100px;
        }

        .up-input-field.short-field {
          max-width: 50px;
          margin-left: 10px;
        }

        .up-input-field.medium-field {
          max-width: 80px;
          margin-left: 10px;
        }

        .up-input-field.long-address-field {
          max-width: 100%;
          min-width: 150px;
        }

        .up-input-field[type="date"] {
          padding: 5px 5px 3px 6px;
        }

        .up-input-field:disabled {
          color: #666;
          background: transparent;
        }

        .up-error {
          color: #fa4848;
          font-size: 10px;
          margin: 1px 0;
          text-align: center;
        }

        .up-personal-rows {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .interests-section {
          margin-top: 10px;
        }

        .interests-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 8px;
        }

        .checkbox-item {
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .checkbox-item input {
          width: 16px;
          height: 16px;
        }

        .checkbox-item label {
          font-size: 14px;
          color: #333;
        }

        @media (max-width: 450px) {
          .up-page-content {
            width: 90vw;
            padding: 5px;
            margin: 5px auto;
            height: calc(100vh - 70px);
          }

          .up-section-title {
            font-size: 14px; 
            color: #333;
          }

          .up-form-label {
            font-size: 14px;
            color: #333;
            min-width: 80px;
          }

          .up-input-field,
          .up-input-field[type="text"],
          .up-input-field[type="email"],
          .up-input-field[type="tel"],
          .up-input-field[type="date"] {
            font-size: 14px;
            padding: 3px;
            color: #333;
          }

          .up-input-field.long-field {
            max-width: 200px;
          }

          .up-input-field.short-field {
            max-width: 50px; 
          }

          .up-input-field.medium-field {
            max-width: 60px; 
          }

          .up-input-field.long-address-field {
            max-width: 150px;
          }

          .up-action-btn {
            font-size: 14px;
            padding: 2px; 
            color: #666; 
          }

          .up-form-columns {
            flex-direction: column;
            gap: 8px;
          }

          .up-form-row {
            flex-direction: column;
            gap: 6px; 
          }

          .up-form-row.no-gap {
            flex-direction: row;
            gap: 0;
          }

          .up-form-row.label-row {
            flex-direction: row;
            gap: 4px;
          }

          .up-avatar,
          .up-profile-pic {
            width: 60px;
            height: 60px;
          }

          .up-user-email {
            font-size: 12px; 
            color: #333;
          }

          .up-error {
            font-size: 10px;
            color: #fa4848;
          }
        }
      `}</style>

      <div className="up-page-container">
        <div className="up-page-content">
          <button className="up-page-close-btn" onClick={handleClose}>
            <FaTimes />
          </button>

          <div className="up-profile-picture">
            {profile.profilePicture ? (
              <img src={profile.profilePicture} alt="Profile" className="up-profile-pic" />
            ) : (
              <div className="up-avatar">
                <FaUser size={20} />
              </div>
            )}
          </div>

          <div className="up-user-info">
            {/* Name removed as requested */}
          </div>

          <div className="up-page-body">
            {formError && <p className="up-error">{formError}</p>}

            {/* Personal Information Section */}
            <div className="up-form-section">
              <div className="up-section-header">
                <h3 className="up-section-title">Personal Information</h3>
                {isEditingPersonal ? (
                  <div className="up-action-buttons">
                    <button type="button" className="up-action-btn save-btn" onClick={() => handleSave('personal')}>
                      <FaSave />
                    </button>
                    <button type="button" className="up-action-btn cancel-btn" onClick={handleCancelPersonal}>
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="up-action-btn edit-btn" onClick={handleStartEditPersonal} disabled={!canEditPersonal}>
                    <FaEdit />
                  </button>
                )}
              </div>
              <div className="up-form-columns">
                <div className="up-form-column">
                  <div className="up-personal-rows" style={{ marginBottom: '-10px'}}>
                    <div className="up-form-row label-row" style={{ marginBottom: '-5px'}}>
                      <label className="up-form-label">Name:</label>
                      <div className="up-form-group">
                        <p className="up-input-field long-field"
                           style={{ padding: '0 5px 0 6px', color: userEnv === 'Test' ? 'orange' : '#333' }}>
                          {profile.firstName} {profile.lastName}
                        </p>
                      </div>
                    </div>
                    <div className="up-form-row label-row">
                      <label className="up-form-label">Email:</label>
                      <div className="up-form-group">
                        <input
                          type="email"
                          name="email"
                          placeholder="Email Address"
                          value={profile.email}
                          onChange={handleInputChange}
                          disabled={isAdmin ? !isEditingPersonal : true}
                          className="up-input-field long-field"
                          style={{ fontSize: '14px' }}
                        />
                      </div>
                    </div>
                    <div className="up-form-row label-row">
                      <label className="up-form-label">Birth Day:</label>
                      <div className="up-form-group">
                        <input
                          type="date"
                          name="dateOfBirth"
                          placeholder="YYYY-MM-DD"
                          value={profile.dateOfBirth}
                          onChange={handleInputChange}
                          disabled={!isEditingPersonal}
                          max={minBirthdayStr}
                          className="up-input-field long-field"
                        />
                      </div>
                    </div>
                    <div className="up-form-row label-row">
                      <label className="up-form-label">Anniversary:</label>
                      <div className="up-form-group">
                        <input
                          type="date"
                          name="anniversary"
                          placeholder="YYYY-MM-DD"
                          value={profile.anniversary}
                          onChange={handleInputChange}
                          disabled={!isEditingPersonal}
                          max={new Date().toISOString().split('T')[0]}
                          className="up-input-field long-field"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Contact Section */}
            <div className="up-form-section" style={{ marginBottom: '-10px'}}>
              <div className="up-section-header">
                <h3 className="up-section-title">Delivery Contact</h3>
                {isEditingDelivery ? (
                  <div className="up-action-buttons">
                    <button type="button" className="up-action-btn save-btn" onClick={() => handleSave('delivery')}>
                      <FaSave />
                    </button>
                    <button type="button" className="up-action-btn cancel-btn" onClick={handleCancelDelivery}>
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="up-action-btn edit-btn" onClick={handleStartEditDelivery} disabled={!canEditDelivery}>
                    <FaEdit />
                  </button>
                )}
              </div>
              <div className="up-form-columns">
                <div className="up-form-column">
                  <div className="up-form-row label-row" style={{ alignItems: 'flex-start' }}>
                    <label className="up-form-label" style={{ paddingTop: '6px' }}>Address:</label>
                    <div className="up-form-group">
                      {isEditingDelivery ? (
                        <>
                          {/* Row 1: PIN Code + House Number */}
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
                            <div style={{ position: 'relative', flex: '1 1 0' }}>
                              <input
                                type="text"
                                name="addressPinCode"
                                placeholder="PIN / Location"
                                value={profile.addressPinCode}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  addrPinRawRef.current = '';
                                  addrCityRawRef.current = '';
                                  setProfile(prev => ({ ...prev, addressPinCode: val }));
                                  if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
                                  if (val.length >= 2) {
                                    setLocationLoading(true);
                                    locationDebounceRef.current = setTimeout(async () => {
                                      try {
                                        if (!allLocalitiesRef.current && !localitiesLoadingRef.current) {
                                          localitiesLoadingRef.current = true;
                                          const res = await fetch('/de-plz.json');
                                          allLocalitiesRef.current = await res.json();
                                          localitiesLoadingRef.current = false;
                                        }
                                        let attempts = 0;
                                        while (localitiesLoadingRef.current && attempts++ < 40) {
                                          await new Promise(r => setTimeout(r, 100));
                                        }
                                        const all = allLocalitiesRef.current || [];
                                        const isNumeric = /^\d+$/.test(val.trim());
                                        const valLower = val.toLowerCase();
                                        let suggestions;
                                        if (isNumeric) {
                                          suggestions = all.filter(r => r[0].startsWith(val)).slice(0, 8).map(r => ({ postcode: r[0], city: r[1], isNumeric: true }));
                                        } else {
                                          const seen = new Set();
                                          suggestions = [];
                                          for (const r of all) {
                                            const name = r[1];
                                            if (name.toLowerCase().startsWith(valLower) && !seen.has(name)) {
                                              seen.add(name);
                                              suggestions.push({ postcode: r[0], city: name, isNumeric: false });
                                              if (suggestions.length === 8) break;
                                            }
                                          }
                                          suggestions.sort((a, b) => a.city.localeCompare(b.city, 'de'));
                                        }
                                        setLocationSuggestions(suggestions);
                                      } catch { setLocationSuggestions([]); }
                                      finally { setLocationLoading(false); }
                                    }, 200);
                                  } else {
                                    setLocationSuggestions([]);
                                    setLocationLoading(false);
                                  }
                                }}
                                onBlur={() => setTimeout(() => setLocationSuggestions([]), 200)}
                                style={{
                                  width: '100%', padding: '5px 6px', border: '1px solid #ddd', fontSize: '13px',
                                  borderRadius: locationSuggestions.length > 0 || locationLoading ? '4px 4px 0 0' : '4px',
                                  boxSizing: 'border-box'
                                }}
                              />
                              {(locationLoading || locationSuggestions.length > 0) && (
                                <div className="autocomplete-dropdown" style={{
                                  position: 'absolute', top: '100%', left: 0, right: 0,
                                  border: '1px solid #ddd', borderTop: 'none',
                                  borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                  zIndex: 30
                                }}>
                                  {locationLoading && <div style={{ padding: '6px 10px', fontSize: '12px', color: '#999' }}>Searching�</div>}
                                  {locationSuggestions.map((s, i) => (
                                    <div key={i}
                                      onMouseDown={(ev) => {
                                        ev.preventDefault();
                                        const label = s.isNumeric ? s.postcode : s.city;
                                        addrPinRawRef.current = s.isNumeric ? s.postcode : '';
                                        addrCityRawRef.current = s.city;
                                        setProfile(prev => ({ ...prev, addressPinCode: label }));
                                        setLocationSuggestions([]);
                                        if (s.isNumeric) streetCacheRef.current[s.postcode] = streetCacheRef.current[s.postcode];
                                      }}
                                      className="autocomplete-item"
                                      style={{ padding: '6px 10px', fontSize: '13px', cursor: 'pointer', borderBottom: i < locationSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                                    >
                                      {s.isNumeric ? (
                                        <><span style={{ fontWeight: '600' }}>{s.postcode}</span><span style={{ margin: '0 5px', color: '#aaa' }}>�</span><span style={{ color: '#555' }}>{s.city}</span></>
                                      ) : (<span>{s.city}</span>)}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <input
                              type="text"
                              name="addressHouseNumber"
                              placeholder="No."
                              value={profile.addressHouseNumber}
                              onChange={handleInputChange}
                              style={{ width: '60px', flexShrink: 0, padding: '5px 6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }}
                            />
                          </div>
                          {/* Row 2: Street Name with autocomplete */}
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              name="addressStreet"
                              placeholder="Street Name"
                              value={profile.addressStreet}
                              onChange={async (e) => {
                                const val = e.target.value;
                                setProfile(prev => ({ ...prev, addressStreet: val }));
                                if (streetDebounceRef.current) clearTimeout(streetDebounceRef.current);
                                if (val.length >= 3) {
                                  setStreetLoading(true);
                                  streetDebounceRef.current = setTimeout(async () => {
                                    try {
                                      const rawPincode = addrPinRawRef.current;
                                      const cityName = addrCityRawRef.current;
                                      const pincodeField = (profile.addressPinCode || '').trim();
                                      const pincodeNumeric = rawPincode || (/^\d{5}$/.test(pincodeField) ? pincodeField : '');
                                      const valLower = val.toLowerCase();
                                      let rows = [];
                                      if (pincodeNumeric) {
                                        if (!streetCacheRef.current[pincodeNumeric]) {
                                          const pages = [];
                                          for (let pg = 1; pg <= 10; pg++) {
                                            const d = await fetch(`https://openplzapi.org/de/Streets?postalCode=${encodeURIComponent(pincodeNumeric)}&pageSize=50&page=${pg}`).then(r => r.json()).catch(() => []);
                                            if (!Array.isArray(d) || d.length === 0) break;
                                            pages.push(...d);
                                            if (d.length < 50) break;
                                          }
                                          streetCacheRef.current[pincodeNumeric] = pages;
                                        }
                                        rows = streetCacheRef.current[pincodeNumeric];
                                      } else if (cityName) {
                                        const d = await fetch(`https://openplzapi.org/de/Streets?name=${encodeURIComponent(val)}&locality=${encodeURIComponent(cityName)}&pageSize=50`).then(r => r.json()).catch(() => []);
                                        rows = Array.isArray(d) ? d : [];
                                      } else {
                                        const d = await fetch(`https://openplzapi.org/de/Streets?name=${encodeURIComponent(val)}&pageSize=50`).then(r => r.json()).catch(() => []);
                                        rows = Array.isArray(d) ? d : [];
                                      }
                                      const filtered = rows.filter(r => (r.name || '').toLowerCase().startsWith(valLower));
                                      const nameCount = {};
                                      filtered.forEach(r => { nameCount[r.name] = (nameCount[r.name] || 0) + 1; });
                                      const seen = new Set(); const suggestions = [];
                                      for (const r of filtered) {
                                        const key = `${r.name}|${r.postalCode}`;
                                        if (seen.has(key)) continue;
                                        seen.add(key);
                                        suggestions.push({ name: r.name, postcode: r.postalCode || '', locality: r.locality || '', showLabel: nameCount[r.name] > 1 });
                                        if (suggestions.length >= 10) break;
                                      }
                                      suggestions.sort((a, b) => a.name.localeCompare(b.name, 'de'));
                                      setStreetSuggestions(suggestions);
                                    } catch { setStreetSuggestions([]); }
                                    finally { setStreetLoading(false); }
                                  }, 400);
                                } else {
                                  setStreetSuggestions([]);
                                  setStreetLoading(false);
                                }
                              }}
                              onBlur={() => setTimeout(() => setStreetSuggestions([]), 200)}
                              style={{
                                width: '100%', padding: '5px 6px', border: '1px solid #ddd', fontSize: '13px',
                                borderRadius: streetSuggestions.length > 0 || streetLoading ? '4px 4px 0 0' : '4px',
                                boxSizing: 'border-box'
                              }}
                            />
                            {(streetLoading || streetSuggestions.length > 0) && (
                              <div className="autocomplete-dropdown" style={{
                                position: 'absolute', top: '100%', left: 0, right: 0,
                                border: '1px solid #ddd', borderTop: 'none',
                                borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                zIndex: 20
                              }}>
                                {streetLoading && <div style={{ padding: '6px 10px', fontSize: '12px', color: '#999' }}>Searching�</div>}
                                {streetSuggestions.map((s, i) => (
                                  <div key={i}
                                    onMouseDown={(ev) => {
                                      ev.preventDefault();
                                      const currentPin = (profile.addressPinCode || '').trim();
                                      const isPincode = /^\d{5}$/.test(currentPin);
                                      const updates = { addressStreet: s.name };
                                      if (!isPincode && s.postcode) {
                                        // searched by city ? replace with pincode
                                        updates.addressPinCode = s.postcode;
                                      }
                                      setProfile(prev => ({ ...prev, ...updates }));
                                      setStreetSuggestions([]);
                                    }}
                                    className="autocomplete-item"
                                    style={{ padding: '6px 10px', fontSize: '13px', cursor: 'pointer', borderBottom: i < streetSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                  >
                                    <span>{s.name}</span>
                                    {s.showLabel && s.postcode && <span style={{ fontSize: '11px', color: '#999', marginLeft: '8px', whiteSpace: 'nowrap' }}>{s.postcode}{s.locality ? ` ${s.locality}` : ''}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <p className="up-input-field long-field" style={{ padding: '5px 5px 3px 6px' }}>
                          {fullAddress}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="up-form-row label-row" style={{ alignItems: isEditingDelivery ? 'flex-start' : 'center' }}>
                    <label className="up-form-label">Phone Number:</label>
                    <div className="up-form-group">
                      {/* View mode */}
                      {!isEditingDelivery && profile.phoneNumber && (
                        <span style={{ fontSize: '13px' }}>{profile.phoneNumber}</span>
                      )}
                      {/* Edit mode */}
                      {isEditingDelivery && canEditPhone && (
                        <div style={{ marginTop: '2px' }}>
                          {/* ISD prefix + input + Ask Code button inline */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                            <span style={{
                              fontSize: '13px', color: '#555', border: '1px solid #ddd',
                              borderRadius: '4px 0 0 4px', padding: '5px 8px', background: '#f5f5f5',
                              borderRight: 'none', whiteSpace: 'nowrap', flexShrink: 0,
                            }}>
                              {upIsdCode}
                            </span>
                            <input
                              type="tel"
                              placeholder="Phone number"
                              value={upPhoneInput}
                              onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 11);
                                setUpPhoneInput(v);
                                setUpPhoneError('');
                                setUpPhoneVerified(false);
                                setUpPhoneCodeSent(false);
                                setUpPhoneCodeInput('');
                              }}
                              style={{
                                flex: 1, fontSize: '13px', padding: '5px 8px',
                                border: '1px solid #ddd', borderRadius: '0 4px 4px 0',
                                boxSizing: 'border-box', minWidth: 0,
                              }}
                            />
                            {/* Ask Code � when number changed and not yet sent */}
                            {upPhoneInput !== ((profile.phoneNumber || '').replace(upIsdCode, '')) && !upPhoneCodeSent && (
                              <button
                                type="button"
                                onClick={handleUpSendPhoneCode}
                                disabled={upPhoneSendingCode || upPhoneInput.length < 5}
                                style={{
                                  fontSize: '10px', padding: '3px 6px',
                                  background: upPhoneSendingCode || upPhoneInput.length < 5 ? '#e0e0e0' : '#fa4848',
                                  color: upPhoneSendingCode || upPhoneInput.length < 5 ? '#aaa' : '#fff',
                                  border: 'none', borderRadius: '4px',
                                  cursor: upPhoneSendingCode || upPhoneInput.length < 5 ? 'default' : 'pointer',
                                  whiteSpace: 'nowrap', flexShrink: 0,
                                }}
                              >
                                {upPhoneSendingCode ? 'Sending...' : 'Verify'}
                              </button>
                            )}
                            {/* Resend � once code already sent, not yet verified */}
                            {upPhoneInput !== ((profile.phoneNumber || '').replace(upIsdCode, '')) && upPhoneCodeSent && !upPhoneVerified && (
                              <button
                                type="button"
                                onClick={() => { setUpPhoneCodeSent(false); handleUpSendPhoneCode(); }}
                                disabled={upPhoneSendingCode || upPhoneResendCountdown > 0 || upPhoneInput.length < 5}
                                style={{
                                  fontSize: '10px', padding: '3px 6px',
                                  background: upPhoneSendingCode || upPhoneResendCountdown > 0 || upPhoneInput.length < 5 ? '#e0e0e0' : '#fa4848',
                                  color: upPhoneSendingCode || upPhoneResendCountdown > 0 || upPhoneInput.length < 5 ? '#aaa' : '#fff',
                                  border: 'none', borderRadius: '4px',
                                  cursor: upPhoneSendingCode || upPhoneResendCountdown > 0 || upPhoneInput.length < 5 ? 'default' : 'pointer',
                                  whiteSpace: 'nowrap', flexShrink: 0,
                                }}
                              >
                                {upPhoneSendingCode ? 'Sending...' : upPhoneResendCountdown > 0 ? `Resend (${upPhoneResendCountdown}s)` : 'Resend Code'}
                              </button>
                            )}
                          </div>
                          {/* 6-digit code � visible only after Ask Code clicked, hidden after verified */}
                          {upPhoneInput !== ((profile.phoneNumber || '').replace(upIsdCode, '')) && upPhoneCodeSent && !upPhoneVerified && (
                            <div style={{ marginBottom: '3px' }}>
                              <input
                                type="text"
                                placeholder="6-digit code"
                                value={upPhoneCodeInput}
                                disabled={upPhoneVerifying}
                                onChange={(e) => {
                                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                                  setUpPhoneCodeInput(v);
                                  setUpPhoneError('');
                                  if (v.length === 6) handleUpVerifyPhoneCode(v);
                                }}
                                style={{
                                  width: '90px', fontSize: '13px', padding: '4px 5px',
                                  border: '1px solid #ddd', borderRadius: '4px',
                                  letterSpacing: '3px', textAlign: 'center',
                                  color: '#333',
                                  background: '#fff',
                                  display: 'block', marginBottom: '3px',
                                }}
                              />
                              {upPhoneError === 'invalid' && <span style={{ fontSize: '11px', color: '#fa4848' }}>Security code is invalid</span>}
                              {upPhoneError && upPhoneError !== 'invalid' && <span style={{ fontSize: '11px', color: '#27ae60' }}>{upPhoneError}</span>}
                              {upPhoneVerifying && <span style={{ fontSize: '11px', color: '#888' }}>Verification in progress...</span>}
                            </div>
                          )}
                          {upPhoneVerified && <span style={{ fontSize: '11px', color: '#27ae60' }}>&#10003; Verified</span>}
                        </div>
                      )}
                      {/* Read-only: editing delivery but canEditPhone is false */}
                      {isEditingDelivery && !canEditPhone && profile.phoneNumber && (
                        <span style={{ fontSize: '13px' }}>{profile.phoneNumber}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cake Interest Section */}
            <div className="up-form-section interests-section" style={{ marginBottom: '-10px'}}>
              <div className="up-section-header">
                <h3 className="up-section-title">Cake Interest:</h3>
                {isEditingCakeInterest ? (
                  <div className="up-action-buttons">
                    <button type="button" className="up-action-btn save-btn" onClick={() => handleSave('cake')}>
                      <FaSave />
                    </button>
                    <button type="button" className="up-action-btn cancel-btn" onClick={handleCancelCakeInterest}>
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="up-action-btn edit-btn" onClick={handleStartEditCakeInterest} disabled={!canEditDelivery}>
                    <FaEdit />
                  </button>
                )}
              </div>
              <div className="interests-grid">
                {cakeSegments.map(segment => (
                  <div key={segment} className="checkbox-item">
                    <input
                      type="checkbox"
                      id={`cake-${segment}`}
                      checked={cakeInterests[segment] || false}
                      onChange={handleInterestChange('cake', segment)}
                      disabled={!isEditingCakeInterest}
                    />
                    <label htmlFor={`cake-${segment}`}>{segment}</label>
                  </div>
                ))}
              </div>
            </div>

            {/* Event Interest Section */}
            <div className="up-form-section interests-section">
              <div className="up-section-header">
                <h3 className="up-section-title">Event Interest:</h3>
                {isEditingEventInterest ? (
                  <div className="up-action-buttons">
                    <button type="button" className="up-action-btn save-btn" onClick={() => handleSave('event')}>
                      <FaSave />
                    </button>
                    <button type="button" className="up-action-btn cancel-btn" onClick={handleCancelEventInterest}>
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="up-action-btn edit-btn" onClick={handleStartEditEventInterest} disabled={!canEditDelivery}>
                    <FaEdit />
                  </button>
                )}
              </div>
              <div className="interests-grid">
                {events.map(event => (
                  <div key={event.name} className="checkbox-item">
                    <input
                      type="checkbox"
                      id={`event-${event.name}`}
                      checked={eventInterests[event.name] || false}
                      onChange={handleInterestChange('event', event.name)}
                      disabled={!isEditingEventInterest}
                    />
                    <label htmlFor={`event-${event.name}`}>{event.name}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfile;